import type { Faro } from "@grafana/faro-core";
import type {
  TraceAPI,
  ContextAPI,
  Span as TraceSpan,
  Tracer,
  Attributes,
} from "@opentelemetry/api";
import type { PushErrorOptions } from "@grafana/faro-core/dist/types/api/exceptions/types";
import type { EventAttributes } from "@grafana/faro-core/dist/types/api/events/types";
import type { PushLogOptions } from "@grafana/faro-core/dist/types/api/logs/types";

export interface Span extends TraceSpan {
  end: () => void;
  duration: [number, number];
  name: string;
}

export class trace {
  private static tracer: Tracer | undefined;
  private static context: any;
  private static trace: any;
  private static root = "init:alp-portal";
  private static parents: Span[] = [];
  private static faro: Faro;

  static init(faro: Faro) {
    if (faro) {
      this.faro = faro;
      if (!this.tracer || !this.context) {
        const otel = faro.api.getOTEL();
        this.tracer = otel?.trace.getTracer("default");
        this.context = otel?.context;
        this.trace = otel?.trace;
        this.initRootSpan();
      }
    }
  }

  private static initRootSpan() {
    this.startSpan(this.root);
    window.addEventListener("beforeunload", () => {
      while (this.parents.length > 0) {
        this.parents.pop()?.end();
      }
    });
  }

  public static startSpan(spanName: string, attributes?: Attributes) {
    if (this.context) {
      try {
        const context = this.trace?.setSpan(
          this.context.active(),
          this.parents[this.parents.length - 1]
        );
        const span = this.tracer?.startSpan(spanName, undefined, context) as Span;
        if (span) {
          this.parents.push(span);
        }
        this.faro.api.pushEvent(`ui-tracking:${spanName}`, {
          ...attributes,
          span_traceId: span?.spanContext().traceId ?? "",
        });
      } catch (e) {
        console.error(e);
      }
    }
  }

  public static endSpan(spanName: string) {
    if (this.context) {
      try {
        let span: Span | undefined = undefined;
        let count = 0;
        do {
          span = this.parents.pop();
          if (span && span?.name === spanName) {
            span?.end();
            this.faro.api.pushMeasurement({
              type: `performance-measure`,
              values: {
                [`ui-tracking-${spanName}-duration`]: Number(
                  span.duration[0] + "." + span.duration[1]
                ),
              },
            });
          }
          ++count;
        } while (span?.name !== spanName && count < 1000);
      } catch (e) {
        console.error(e);
      }
    }
  }

  public static postError(error: Error, name?: string, options?: PushErrorOptions) {
    if (name) {
      error.name = name;
    }
    this.faro?.api.pushError(error, options);
  }

  public static pushEvent(name: string, attributes?: EventAttributes, domain?: string) {
    this.faro?.api.pushEvent(name, attributes, domain);
  }

  public static pushLog(message: [], options?: PushLogOptions) {
    this.faro?.api.pushLog(message, options);
  }

  /**
   * Report a numeric measurement. `type` lands in `measurement_type` and each
   * key of `values` becomes its own column in the collector's document, so keep
   * the key set small and stable rather than encoding data into key names.
   */
  public static pushMeasurement(type: string, values: Record<string, number>) {
    const finite = Object.entries(values).filter(([, v]) => Number.isFinite(v));
    if (finite.length === 0) {
      return;
    }
    this.faro?.api.pushMeasurement({ type, values: Object.fromEntries(finite) });
  }

  public static performanceMark(markName: string, options?: PerformanceMarkOptions) {
    performance.mark(markName, options);
  }

  public static performanceMeasure(measureName: string, startMark: string, endMark: string) {
    performance.measure(measureName, startMark, endMark);
  }

  public static setUser = (user_id: any, user_name: any, user_roles: any) => {
    user_id = !user_id ? "anonymoususer" : user_id;
    user_name = !user_name ? "anonymoususer" : user_name;
    this.faro?.api.setUser({
      id: user_id,
      username: user_name,
      attributes: {
        name: user_name,
        roles: JSON.stringify(user_roles),
      },
    });
  };

  public static setView = (view_name: string) => {
    view_name = !view_name ? "default" : view_name;
    this.faro?.api.setView({ name: view_name });
  };
}
